// TaskReminder.jsx - Task and reminder component
import React from 'react';
import { Card, List, Button, Tag, Typography, Space, Checkbox } from 'antd';
import { 
  BellOutlined,
  PlusOutlined,
  CalendarOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

const TaskReminder = () => {
  const [tasks, setTasks] = React.useState([
    {
      id: 1,
      title: 'Review portfolio feedback',
      priority: 'high',
      dueDate: '2024-02-15',
      completed: false,
      category: 'review'
    },
    {
      id: 2,
      title: 'Update project documentation',
      priority: 'medium',
      dueDate: '2024-02-18',
      completed: false,
      category: 'documentation'
    },
    {
      id: 3,
      title: 'Prepare for client meeting',
      priority: 'high',
      dueDate: '2024-02-16',
      completed: true,
      category: 'meeting'
    },
    {
      id: 4,
      title: 'Backup project files',
      priority: 'low',
      dueDate: '2024-02-20',
      completed: false,
      category: 'maintenance'
    }
  ]);

  const toggleTask = (taskId) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'orange';
      case 'low': return 'green';
      default: return 'default';
    }
  };

  const incompleteTasks = tasks.filter(task => !task.completed);
  const completedTasks = tasks.filter(task => task.completed);

  return (
    <Card 
      title={
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BellOutlined className="text-accent-blue" />
            <span>Tasks & Reminders</span>
          </div>
          <Button 
            type="text" 
            icon={<PlusOutlined />}
            className="text-accent-blue hover:bg-accent-blue/10"
          >
            Add Task
          </Button>
        </div>
      }
      className="shadow-medium border border-border-light rounded-xl h-full"
    >
      <div className="space-y-4">
        {/* Pending Tasks */}
        {incompleteTasks.length > 0 && (
          <div>
            <Text className="text-text-secondary text-sm font-medium uppercase tracking-wide">
              Pending ({incompleteTasks.length})
            </Text>
            <List
              size="small"
              dataSource={incompleteTasks}
              renderItem={(task) => (
                <List.Item className="hover:bg-bg-secondary rounded-lg px-2 py-1">
                  <div className="flex items-center space-x-3 w-full">
                    <Checkbox 
                      checked={task.completed}
                      onChange={() => toggleTask(task.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <Text className="text-text-primary">
                          {task.title}
                        </Text>
                        <Space size="small">
                          <Tag 
                            color={getPriorityColor(task.priority)}
                            size="small"
                          >
                            {task.priority}
                          </Tag>
                          <Tag 
                            color="default" 
                            size="small"
                            icon={<CalendarOutlined />}
                          >
                            {new Date(task.dueDate).toLocaleDateString()}
                          </Tag>
                        </Space>
                      </div>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div>
            <Text className="text-text-secondary text-sm font-medium uppercase tracking-wide">
              Completed ({completedTasks.length})
            </Text>
            <List
              size="small"
              dataSource={completedTasks}
              renderItem={(task) => (
                <List.Item className="hover:bg-bg-secondary rounded-lg px-2 py-1 opacity-60">
                  <div className="flex items-center space-x-3 w-full">
                    <Checkbox 
                      checked={task.completed}
                      onChange={() => toggleTask(task.id)}
                    />
                    <div className="flex-1">
                      <Text className="text-text-secondary line-through">
                        {task.title}
                      </Text>
                    </div>
                  </div>
                </List.Item>
              )}
            />
          </div>
        )}

        {tasks.length === 0 && (
          <div className="text-center py-8">
            <BellOutlined className="text-4xl text-neutral-300 mb-2" />
            <Text className="text-text-secondary">
              No tasks yet. Add your first task to get started!
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

export default TaskReminder;